import {
    AnySource,
    BookingSourceType,
    CatalogSource,
    CatalogSourceParent,
    ClientAccountSource,
    EmailResultsSource,
    ListSource,
    NotificationSourceType,
    PreviousBookingSource,
    PsyCollectionSource,
    SavedItem,
    SurveyResultsSource,
} from "modules/FunnelAnalytics/interfaces"
import { APP_STATUSES } from "utils/AppStatusContext"

export function toItem(saved: string): SavedItem {
    if (saved.startsWith(APP_STATUSES.psy_collection)) {
        const [status, collectionId] = saved.split(":") as [APP_STATUSES.psy_collection, string]
        return {
            status,
            collectionId,
        }
    }
    if (saved.startsWith(APP_STATUSES.profile)) {
        const [status, emailSource] = saved.split(":") as [
            APP_STATUSES.psy_collection,
            NotificationSourceType | undefined,
        ]
        if (emailSource) {
            return {
                status,
                emailSource,
            }
        }
        return {
            status,
        }
    }
    if (saved.startsWith(APP_STATUSES.survey_results)) {
        const [status, express] = saved.split(":") as [APP_STATUSES.survey_results, boolean]
        return {
            status,
            express,
        }
    }
    return {
        status: saved as BookingSourceType,
    }
}

export function itemToString(item: SavedItem) {
    if (item.collectionId) {
        return `${item.status}:${item.collectionId}`
    }
    if (item.emailSource) {
        return `${item.status}:${item.emailSource}`
    }
    if (item.express) {
        return `${item.status}:${item.express.toString()}`
    }
    return item.status
}

export function createBookingSource(item?: SavedItem, parent?: SavedItem): AnySource {
    switch (item?.status) {
        case APP_STATUSES.search_page:
            return createCatalogSource(createBookingSource(parent))
        case APP_STATUSES.list:
            return createListSource()
        case APP_STATUSES.psy_collection:
            if (item.collectionId) {
                return createCollectionSource(item.collectionId)
            } else {
                break
            }
        case APP_STATUSES.survey_results:
            return createSurveyResultsSource(item?.express)
        case APP_STATUSES.results_from_email:
            return createEmailResultsSource()
        case APP_STATUSES.profile:
            return createProfileSource(item?.emailSource)
        case APP_STATUSES.booking:
            return createPreviousBookingSource()
        case APP_STATUSES.client_account:
            return createClientAccountBookingSource()
    }
    return {
        source: "unknown",
    }
}

export function isAllowedToBeBookingSource(status: APP_STATUSES): status is BookingSourceType {
    return [
        APP_STATUSES.search_page,
        APP_STATUSES.list,
        APP_STATUSES.psy_collection,
        APP_STATUSES.survey_results,
        APP_STATUSES.results_from_email,
        APP_STATUSES.profile,
        APP_STATUSES.booking,
        APP_STATUSES.client_account,
    ].includes(status)
}

function createCatalogSource(parent: CatalogSourceParent): CatalogSource {
    return {
        source: APP_STATUSES.search_page,
        data: {
            parent,
        },
    }
}

function createCollectionSource(collectionId: string): PsyCollectionSource {
    return {
        source: APP_STATUSES.psy_collection,
        data: {
            collectionId,
        },
    }
}

function createListSource(): ListSource {
    return {
        source: APP_STATUSES.list,
    }
}

function createProfileSource(emailSource?: string): ListSource {
    if (emailSource) {
        return {
            source: APP_STATUSES.profile,
            data: {
                emailSource,
            },
        }
    }
    return {
        source: APP_STATUSES.profile,
    }
}

function createSurveyResultsSource(express?: boolean): SurveyResultsSource {
    if (express) {
        return {
            source: APP_STATUSES.survey_results,
            data: {
                express,
            },
        }
    }
    return {
        source: APP_STATUSES.survey_results,
    }
}

function createEmailResultsSource(): EmailResultsSource {
    return {
        source: APP_STATUSES.results_from_email,
    }
}

function createPreviousBookingSource(): PreviousBookingSource {
    return {
        source: APP_STATUSES.booking,
    }
}

function createClientAccountBookingSource(): ClientAccountSource {
    return {
        source: APP_STATUSES.client_account,
    }
}
